import { useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";
import { Plus } from "lucide-react";

import { cn } from "@/lib/utils";

import type { EditorField } from "./fields-context";

export interface FieldMenuHandle {
	onKeyDown(event: KeyboardEvent): boolean;
}

interface FieldMenuProps {
	items: EditorField[];
	command(item: EditorField): void;
	query: string;
	onCreate?: () => void;
	ref: Ref<FieldMenuHandle>;
}

export function FieldMenu({ items, command, query, onCreate, ref }: FieldMenuProps) {
	const [selected, setSelected] = useState(0);

	const count = items.length + (onCreate === undefined ? 0 : 1);
	const createIndex = onCreate === undefined ? -1 : items.length;

	useEffect(() => {
		setSelected(0);
	}, [items]);

	const run = (index: number) => {
		if (index === createIndex) {
			onCreate?.();

			return;
		}

		const item = items.at(index);

		if (item !== undefined) {
			command(item);
		}
	};

	useImperativeHandle(ref, () => ({
		onKeyDown(event) {
			if (count === 0) {
				return false;
			}

			if (event.key === "ArrowDown") {
				setSelected((current) => (current + 1) % count);

				return true;
			}

			if (event.key === "ArrowUp") {
				setSelected((current) => (current - 1 + count) % count);

				return true;
			}

			if (event.key === "Enter") {
				run(selected);

				return true;
			}

			return false;
		},
	}));

	if (count === 0) {
		return (
			<div className="rounded-lg border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-overlay">
				Nenhum campo encontrado
			</div>
		);
	}

	const createLabel = query.trim() === "" ? "Novo campo" : `Novo campo "${query}"`;

	return (
		<div className="flex min-w-48 flex-col overflow-hidden rounded-lg border bg-popover p-1 shadow-overlay">
			{items.map((item, index) => (
				<button
					key={item.id}
					type="button"
					className={cn(
						"flex items-baseline justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm",
						index === selected && "bg-accent text-accent-foreground",
					)}
					onMouseEnter={() => setSelected(index)}
					onClick={() => command(item)}
				>
					<span>{item.label}</span>
					{!!item.value && (
						<span className="max-w-40 truncate text-xs text-muted-foreground">{item.value}</span>
					)}
				</button>
			))}

			{onCreate !== undefined && (
				<button
					type="button"
					className={cn(
						"flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground",
						selected === createIndex && "bg-accent text-accent-foreground",
					)}
					onMouseEnter={() => setSelected(createIndex)}
					onClick={onCreate}
				>
					<Plus className="size-3.5" />
					<span className="truncate">{createLabel}</span>
				</button>
			)}
		</div>
	);
}
